import { getOrganizationId } from './organization.schema';
import type { SchemaOrgNode } from './schema-org-node.model';

export interface BlogPostingEntryData {
  pageType?: string;
  pageTitle?: string;
  title?: string;
  postDate?: string;
  dateCreated?: string;
  dateUpdated?: string;
  blogSummary?: string;
  featuredEntryImage?: Array<{ url?: string; altText?: string }>;
}

export interface BlogPostingSchemaData {
  entryData: BlogPostingEntryData;
  pageUrl: string;
  siteUrl: string;
  organizationName: string;
}

const BLOGPOSTING_ID_SUFFIX = '#blogposting';

interface SafeValue {
  changingThisBreaksApplicationSecurity?: string;
}

function isSafeValue(value: unknown): value is SafeValue {
  return (
    value !== null &&
    value !== undefined &&
    typeof value === 'object' &&
    'changingThisBreaksApplicationSecurity' in value
  );
}

function extractHtmlFromSafeValue(html: unknown): string {
  if (!html) return '';
  if (typeof html === 'string') return html;
  if (isSafeValue(html) && html.changingThisBreaksApplicationSecurity) {
    return html.changingThisBreaksApplicationSecurity;
  }
  return '';
}

const STRIP_HTML_MAX_LENGTH = 10_000;

function stripHtml(input: unknown): string {
  const raw = extractHtmlFromSafeValue(input);
  if (!raw) return '';
  const bounded = raw.length > STRIP_HTML_MAX_LENGTH ? raw.slice(0, STRIP_HTML_MAX_LENGTH) : raw;
  return bounded
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<span[^>]*class="sr-only"[^>]*>[\s\S]*?<\/span>/gi, '')
    .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim();
}

function toIsoDate(input: string | undefined): string | undefined {
  if (!input) return undefined;
  const parsed = Date.parse(input);
  if (Number.isNaN(parsed)) return undefined;
  return new Date(parsed).toISOString();
}

/**
 * Builds the BlogPosting node for a blog detail page. Returns null when the
 * entry is not a blog or when no headline can be derived — these are the only
 * two fields Google requires (`headline`) or that we cannot meaningfully fill
 * (`pageType` gate keeps the node off non-blog routes).
 *
 * `articleBody` is intentionally NOT emitted: Google's Article/BlogPosting rich
 * result lists it as optional, and our prior CMS-field-allowlist aggregator
 * silently mis-fired on blog templates that route body prose through fields
 * outside the allowlist (e.g. `sideProductPicker.sideContent`). Dropping the
 * field eliminates the silent-failure mode without affecting rich-result
 * eligibility — Google already extracts body content from the rendered HTML.
 *
 * SSR-safe: pure function, no DOM access, no Angular DI.
 */
export function getBlogPostingSchema(data: BlogPostingSchemaData | null): SchemaOrgNode | null {
  if (!data || !data.entryData || !data.pageUrl) return null;
  if (data.entryData.pageType !== 'blog') return null;

  // Prefer the post `title` over the SEO `pageTitle` — schema.org `headline`
  // is the article's title, and `pageTitle` typically carries an "… | Brand"
  // suffix meant for browser tabs/SERP. Brand attribution is already conveyed
  // by `publisher`. Fall back to `pageTitle` only when `title` is missing.
  const headline = stripHtml(data.entryData.title || data.entryData.pageTitle);
  if (!headline) return null;

  const datePublished = toIsoDate(data.entryData.postDate || data.entryData.dateCreated);
  const dateModified = toIsoDate(data.entryData.dateUpdated) ?? datePublished;

  const orgRef = {
    '@id': getOrganizationId(data.siteUrl),
    '@type': 'EducationalOrganization' as const,
    name: data.organizationName,
  };

  const node: SchemaOrgNode = {
    '@type': 'BlogPosting',
    '@id': `${data.pageUrl}${BLOGPOSTING_ID_SUFFIX}`,
    headline,
    mainEntityOfPage: { '@type': 'WebPage', '@id': data.pageUrl },
    author: orgRef,
    publisher: orgRef,
  };

  if (datePublished) node['datePublished'] = datePublished;
  if (dateModified) node['dateModified'] = dateModified;

  const description = stripHtml(data.entryData.blogSummary);
  if (description) node['description'] = description;

  const image = data.entryData.featuredEntryImage?.[0]?.url;
  if (image) node['image'] = image;

  return node;
}
